import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import GreenIcon from 'assets/icons/jobs/Candidate_Green_Small.png';
import YellowIcon from 'assets/icons/jobs/Candidate_Yellow_Small.png';
import BlueIcon from 'assets/icons/jobs/Candidate_Blue_Small.png';
import RedIcon from 'assets/icons/jobs/Candidate_Red_Small.png';
import GreyIcon from 'assets/icons/jobs/Candidate_Grey_Small.png';
import Radio from '@mui/material/Radio';
import { LinkCandidateToJob } from 'services/CandidatesService';
import CandidatePicker from 'components/Pickers/CandidatePicker';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import { GetCachedRatingReasons } from 'services/ConfigurationService';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

interface Props {
  open: boolean;
  jobId: number;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

export default function LinkJobToCandidateDialog({
  open,
  jobId,
  closeHandler,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [candidateId, setCandidateId] = useState(0);
  const [ratingId, setRatingId] = useState(3);
  const [ratingReason, setRatingReason] = useState('');
  const [source, setSource] = useState('');
  const [reasonMap, setReasonMap] = useState<Record<number, string[]>>({});

  useEffect(() => {
    if (!open) {
      setCandidateId(0);
      setRatingId(3);
      setSource('');
    }
  }, [open]);

  useEffect(() => {
    const getData = async () => {
      const res = await GetCachedRatingReasons(false);
      if (res) {
        const green: string[] = [];
        const yellow: string[] = [];
        const blue: string[] = [];
        const red: string[] = [];
        for (let i = 0; i < res.length; i++) {
          const r = res[i];
          if (r.actionType === 4) green.push(r.value);
          if (r.actionType === 2) yellow.push(r.value);
          if (r.actionType === 1) blue.push(r.value);
          if (r.actionType === 0) red.push(r.value);
        }

        green.sort();
        yellow.sort();
        blue.sort();
        red.sort();
        setReasonMap({
          4: green,
          2: yellow,
          1: blue,
          0: red,
        });
      }
    };
    getData();
  }, []);

  const linkToJobCallback = useCallback(async () => {
    loadingHandler?.(true);
    const res = await LinkCandidateToJob(
      candidateId,
      jobId,
      ratingId,
      ratingReason,
      source,
      errorHandler
    );
    if (res) {
      closeHandler();
      successHandler?.('Candidate Linked To Job');
    }
    loadingHandler?.(false);
  }, [
    candidateId,
    jobId,
    ratingId,
    ratingReason,
    source,
    loadingHandler,
    successHandler,
    errorHandler,
    closeHandler,
  ]);

  useEffect(() => {
    setRatingReason('');
  }, [ratingId]);

  const reasonOptions = useMemo<string[]>(() => {
    if (ratingId !== 3) return reasonMap[ratingId];
    return [];
  }, [ratingId, reasonMap]);

  const onReasonChange = useCallback((event: any, value: string | null) => {
    setRatingReason(value ?? '');
  }, []);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>Link Candidate To Job</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt="10px">
          <CandidatePicker
            triggerFocus={open}
            componentId="LinkJobToCandidateDialog_CandidatePicker"
            value={candidateId}
            onSelectCallback={(c) => setCandidateId(c ? c.id : 0)}
          />
          <Box display="flex" justifyContent="center">
            <Box sx={{ cursor: 'pointer' }} onClick={() => setRatingId(4)}>
              <Box textAlign="center" height="32px">
                <img src={GreenIcon} alt="Green" />
              </Box>
              <Radio name="green" checked={ratingId === 4} />
            </Box>
            <Box sx={{ cursor: 'pointer' }} onClick={() => setRatingId(2)}>
              <Box textAlign="center" height="32px">
                <img src={YellowIcon} alt="Yellow" />
              </Box>
              <Radio name="yellow" checked={ratingId === 2} />
            </Box>
            <Box sx={{ cursor: 'pointer' }} onClick={() => setRatingId(1)}>
              <Box textAlign="center" height="32px">
                <img src={BlueIcon} alt="Blue" />
              </Box>
              <Radio name="blue" checked={ratingId === 1} />
            </Box>
            <Box sx={{ cursor: 'pointer' }} onClick={() => setRatingId(0)}>
              <Box textAlign="center" height="32px">
                <img src={RedIcon} alt="Red" />
              </Box>
              <Radio name="red" checked={ratingId === 0} />
            </Box>
            <Box sx={{ cursor: 'pointer' }} onClick={() => setRatingId(3)}>
              <Box textAlign="center" height="32px">
                <img src={GreyIcon} alt="Grey" />
              </Box>
              <Radio name="grey" checked={ratingId === 3} />
            </Box>
          </Box>
          <Autocomplete
            size="small"
            options={reasonOptions}
            isOptionEqualToValue={(o, v) => o === v}
            onChange={onReasonChange}
            value={ratingReason}
            sx={{ display: ratingId === 3 ? 'none' : undefined }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Reason"
                slotProps={{ htmlInput: { ...params.inputProps, 'data-lpignore': 'true' } }}
              />
            )}
          />
          <RWTextFieldComponent
            label="Source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={candidateId === 0}
          onClick={linkToJobCallback}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
